import { useFloating } from '@floating-ui/react-dom-interactions';
import Input from '@shared/components/InputGroup/Input';
import cx from 'classnames';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowDown } from 'assets/icons/ArrowDown.svg';
import { getDefaultDateInputFloatingProps } from 'components/DateInput/utils';
import useHideOnOutsideClick from 'hooks/useHideOnOutsideClick';

const STEP_MIN = 15;

interface StartTimePickerProps {
  value: Date | null;
  onChange: (time: Date) => void;
  name: string;
  disabledBeforeTime?: Date;
  placeholder?: string;
  containerClassName?: string;
  // Fired on the open transition (closed → open) only, never on close. #8070 BUG-001: lets the
  // parent auto-fill the start date the instant the field is clicked so the disabledBeforeTime
  // cutoff filters past slots on the very first open, before any time is selected.
  onOpen?: () => void;
}

interface Option {
  time: Date;
  label: string;
}

export const buildStartTimeOptions = (disabledBeforeTime?: Date): Option[] => {
  const baseDate = new Date();
  baseDate.setHours(0, 0, 0, 0);
  const options: Option[] = [];
  const cutoffMinutes = disabledBeforeTime ? disabledBeforeTime.getHours() * 60 + disabledBeforeTime.getMinutes() : -1;

  for (let minute = 0; minute < 24 * 60; minute += STEP_MIN) {
    if (minute <= cutoffMinutes) continue;
    const time = new Date(baseDate.getTime() + minute * 60 * 1000);
    options.push({ time, label: moment(time).format('HH:mm') });
  }
  return options;
};

const StartTimePicker = ({
  value,
  onChange,
  name,
  disabledBeforeTime,
  placeholder,
  containerClassName,
  onOpen,
}: StartTimePickerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { x, y, reference, floating, strategy } = useFloating(getDefaultDateInputFloatingProps('bottom'));

  useEffect(() => {
    if (!isOpen || !pickerRef.current) return;
    requestAnimationFrame(() => {
      if (!pickerRef.current) return;
      const selected = pickerRef.current.querySelector<HTMLButtonElement>('[data-selected="true"]');
      if (selected) {
        selected.scrollIntoView({ block: 'center', behavior: 'instant' });
        return;
      }
      // No selection: scroll to option nearest current wall-clock time
      const now = new Date();
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      const buttons = Array.from(pickerRef.current.querySelectorAll<HTMLButtonElement>('button[data-minutes]'));
      const closest = buttons.reduce<HTMLButtonElement | null>((best, btn) => {
        const mins = Number(btn.dataset.minutes);
        const bestMins = best ? Number(best.dataset.minutes) : Infinity;
        return Math.abs(mins - nowMinutes) < Math.abs(bestMins - nowMinutes) ? btn : best;
      }, null);
      closest?.scrollIntoView({ block: 'center', behavior: 'instant' });
    });
  }, [isOpen]);

  useHideOnOutsideClick({ ref: pickerRef, onClickOutside: () => setIsOpen(false), additionalSelector: `#${name}` });

  const options = buildStartTimeOptions(disabledBeforeTime);
  const displayValue = value ? moment(value).format('HH:mm') : '';

  const handleSelect = (option: Option) => {
    onChange(option.time);
    setIsOpen(false);
  };

  return (
    <div className={cx('w-full xs:w-44', containerClassName)} ref={wrapperRef}>
      <Input
        name={name}
        id={name}
        inputSize="md"
        className="cursor-pointer truncate rounded-lg !bg-gray-10 text-sm font-light"
        containerClassName={cx(
          '!border-gray-20 focus-within:!border-primary-50 rounded-2xl text-current',
          isOpen && '!border-primary-50'
        )}
        variant="white-bg"
        autoComplete="off"
        iconClassName={cx('stroke-1 h-5 w-5 stroke-current cursor-pointer', isOpen && 'rotate-180')}
        isRounded
        onContainerClick={() =>
          setIsOpen((prev) => {
            if (!prev) onOpen?.();
            return !prev;
          })
        }
        readOnly
        icon={ArrowDown}
        value={displayValue}
        placeholder={placeholder}
        ref={reference}
      />
      {isOpen && (
        <div
          ref={(el) => {
            pickerRef.current = el;
            floating(el);
          }}
          style={{ position: strategy, top: y ?? 0, left: x ?? 0, width: wrapperRef.current?.offsetWidth }}
          className="z-dropdown flex max-h-80 flex-col gap-1 overflow-y-auto rounded-2xl bg-white p-4 shadow-dropdown"
        >
          {options.length === 0 ? (
            <p className="text-center text-size-sm text-gray-60">{t('schedule_meeting.no_time_options')}</p>
          ) : (
            options.map((option) => {
              const isSelected =
                !!value &&
                value.getHours() === option.time.getHours() &&
                value.getMinutes() === option.time.getMinutes();
              const optionMinutes = option.time.getHours() * 60 + option.time.getMinutes();
              return (
                <button
                  type="button"
                  key={option.time.getTime()}
                  data-selected={isSelected ? 'true' : undefined}
                  data-minutes={optionMinutes}
                  onClick={() => handleSelect(option)}
                  className={cx(
                    'flex min-h-[32px] w-full items-center justify-center whitespace-nowrap px-3 py-1.5 text-size-sm font-light',
                    isSelected ? 'rounded-full bg-primary-50 text-white' : 'rounded-xl text-black hover:bg-gray-10'
                  )}
                >
                  {option.label}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default StartTimePicker;
